
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const ParowPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Parow page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Parow Painters",
      "description": "Professional painting contractors in Parow, Western Cape. Get quotes for interior, exterior, roof and commercial painting services.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/parow-painters`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Parow",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8908",
        "longitude": "18.6158"
      },
      "serviceArea": {
        "@type": "City",
        "name": "Parow"
      },
      "services": [
        "Residential Interior Painting",
        "Exterior House Painting",
        "Roof Painting",
        "Commercial Painting",
        "Heritage Home Restoration",
        "Surface Preparation"
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Parow Painters - Expert Painting Contractors Western Cape | Free Quotes";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting contractors in Parow, Western Cape. Get free quotes for interior, exterior, roof and commercial painting. Heritage home specialists, quality guaranteed.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/1f3d9cb23d244519189e70241eef0fb3.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="block">Parow</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get quotes from the best painting contractors in Parow
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Expert Painting Services in Parow
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Discover Parow's most trusted painting contractors through our comprehensive network. We connect homeowners and businesses with skilled professionals who understand the unique character of this established Cape Town suburb and its rich architectural heritage.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our Parow painting specialists excel in both residential and commercial projects, from heritage home restorations to modern property makeovers. Each contractor brings years of experience working specifically in the Parow area and a deep commitment to exceptional craftsmanship that respects the neighborhood's distinctive character.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're updating a family home in Parow North, renovating a business in the commercial district, or maintaining a property investment throughout the greater Parow area, our network ensures you'll find the perfect painting professional who understands your specific needs and local requirements.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Heritage Home Specialists</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Modern Property Updates</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Commercial Painting</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Quality Guaranteed</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20established%20residential%20home%20in%20Parow%20with%20mature%20gardens%20and%20traditional%20Cape%20architecture%2C%20quality%20painting%20equipment%2C%20clean%20professional%20appearance%2C%20bright%20natural%20lighting&width=600&height=400&seq=parow-painting-work&orientation=landscape"
                alt="Professional painters working in Parow"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Parow Painting Heritage */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Parow's Rich Architectural Heritage and Painting Traditions
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6">
                Parow's established neighborhoods showcase a fascinating blend of architectural styles that span several decades of Cape Town's development. From the charming period homes with their distinctive features to the well-maintained family residences that define Parow's residential character, each property tells a story that our painting contractors help preserve and enhance.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The mature tree-lined streets of Parow create a unique microclimate that affects paint selection and application techniques. Our network of Parow painting professionals understands how the area's established vegetation, seasonal weather patterns, and the Western Cape's coastal influences impact paint performance and longevity.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Working in Parow requires an appreciation for the community's commitment to maintaining property values and neighborhood aesthetics. Our contractors bring not only technical expertise but also an understanding of the pride Parow residents take in their homes and the importance of quality workmanship that enhances the area's appeal.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Parow%20South%20Africa%20established%20neighborhood%20with%20heritage%20homes%2C%20mature%20oak%20trees%2C%20traditional%20Cape%20architecture%2C%20well-maintained%20gardens%2C%20suburban%20character%2C%20professional%20painting%20work&width=600&height=400&seq=parow-heritage&orientation=landscape"
                alt="Parow heritage neighborhood"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Parow Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Parow Painting Services
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored painting solutions that respect Parow's architectural character and community standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Parow Heritage Home Restoration</h5>
              <p className="text-gray-600">Specialized painting services for Parow's period homes, using appropriate techniques and materials that preserve historical character while providing modern protection and beauty.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Parow Residential Exterior Painting</h5>
              <p className="text-gray-600">Expert exterior painting services designed specifically for Parow's established homes, considering the area's mature landscaping and neighborhood aesthetic standards.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Parow Interior Transformation</h5>
              <p className="text-gray-600">Professional interior painting that enhances the character of Parow homes, from period features to modern updates, creating beautiful living spaces for families.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Parow Commercial Painting</h5>
              <p className="text-gray-600">Professional painting services for Parow's business district, offices, and commercial properties, maintaining the professional appearance that supports local commerce.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-brush-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Parow Specialty Finishes</h5>
              <p className="text-gray-600">Decorative painting and specialty finishes that complement Parow's architectural diversity, from traditional techniques to contemporary design elements.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Parow Surface Preparation</h5>
              <p className="text-gray-600">Meticulous surface preparation services that address the specific challenges of Parow's established properties, ensuring optimal paint adhesion and longevity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Parow Painters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Parow Painting Network
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connecting you with Parow's most experienced and trusted painting professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Parow Local Knowledge</h5>
              <p className="text-gray-600">Our contractors have extensive experience working in Parow, understanding the area's unique architectural features, community standards, and local building requirements.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Parow Quality Standards</h5>
              <p className="text-gray-600">All our Parow painters meet the high quality standards expected by this established community, delivering workmanship that enhances property values and neighborhood appeal.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Parow Community Respect</h5>
              <p className="text-gray-600">Reliable contractors who understand and respect Parow's residential character, working considerately within the community and maintaining excellent neighbor relations.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Parow Value Investment</h5>
              <p className="text-gray-600">Competitive pricing that reflects the quality expectations of Parow property owners, ensuring your painting investment enhances your property's value and appeal.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Parow Area Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Comprehensive Parow Area Coverage
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Parow North</h6>
              <p className="text-gray-600">Specialized painting services for the established residential areas of Parow North, including heritage homes and well-maintained family properties.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Parow Central</h6>
              <p className="text-gray-600">Professional painting for Parow's commercial district and mixed-use areas, supporting local businesses and maintaining the area's professional appearance.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Greater Parow</h6>
              <p className="text-gray-600">Comprehensive painting services throughout the broader Parow area, including surrounding neighborhoods and developing residential zones.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Parow Painting Quotes
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to enhance your Parow property? Connect with experienced painting contractors who understand your neighborhood's unique character and requirements
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Parow Residents Say
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Parow homeowners who found their perfect painting contractor through our trusted network
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default ParowPage;
